import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { GigDetail } from '../../types/content';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import { PageLoading } from '../../components/UI/LoadingOverlay';

const formatPrice = (gig: GigDetail) => {
  if (gig.priceAmount == null) return 'Rate on request';
  const amount = new Intl.NumberFormat('en-IN', { style: 'currency', currency: gig.currency || 'INR', maximumFractionDigits: 0 }).format(gig.priceAmount);
  return gig.priceType === 'Hourly' ? `${amount} / hour` : `${amount} total`;
};

const GigDetailPage: React.FC = () => {
  const { slug } = useParams();
  const [gig, setGig] = useState<GigDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    contentApi.getGigBySlug(slug).then((r) => setGig(r.data.gig)).catch(() => setError('This gig is not available.'));
  }, [slug]);

  if (error) {
    return <div className="max-w-3xl mx-auto px-4 py-16 p-3 rounded-md bg-error-50 text-error-600 border border-error-200">{error}</div>;
  }
  if (!gig) return <PageLoading label="Loading gig…" />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <p className="text-sm text-primary-600 font-medium">{gig.clientName}</p>
      <h1 className="mt-3 text-4xl md:text-5xl font-bold text-gray-900 tracking-tight leading-tight">{gig.title}</h1>
      {gig.subtitle && <p className="mt-4 text-xl text-gray-600">{gig.subtitle}</p>}
      {gig.summary && <p className="mt-4 text-lg text-gray-700 leading-relaxed">{gig.summary}</p>}
      <div className="mt-6 flex flex-wrap gap-3 text-sm text-gray-600">
        <span>{gig.teamType} gig</span>
        {gig.duration && <span>{gig.duration}</span>}
        {gig.weeklyHours && <span>{gig.weeklyHours} / week</span>}
        <span>{formatPrice(gig)}</span>
      </div>
      {gig.applyUrl && (
        <div className="mt-6">
          <a href={gig.applyUrl} target="_blank" rel="noreferrer">
            <Button>External apply</Button>
          </a>
        </div>
      )}
      <div className="mt-6">
        <Button onClick={() => contentApi.applyGig(gig.id).then(() => setError(null)).catch((err: unknown) => setError(err instanceof Error ? err.message : 'Unable to apply.'))}>Apply on IDCoder</Button>
      </div>

      <div className="mt-10 grid gap-6">
        {gig.coverImageUrl && <img src={gig.coverImageUrl} alt="" className="w-full rounded-2xl" />}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900">Tech stack</h2>
          <p className="mt-2 text-gray-700 leading-relaxed whitespace-pre-wrap">{gig.techStack || 'Not specified.'}</p>
        </Card>
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900">Job description</h2>
          <p className="mt-2 text-gray-700 leading-relaxed whitespace-pre-wrap">{gig.jobDescription || 'No job description yet.'}</p>
        </Card>
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900">Who can apply</h2>
          <p className="mt-2 text-gray-700 leading-relaxed whitespace-pre-wrap">{gig.whoCanApply || 'Open to qualified applicants.'}</p>
        </Card>
      </div>
    </div>
  );
};

export default GigDetailPage;
